import type { JWTPayload, TokenInfo, CSRFTokenInfo } from '@/types/api'

/**
 * Base64 URL decode function
 */
function base64UrlDecode(str: string): string {
  // Replace URL-safe characters
  str = str.replace(/-/g, '+').replace(/_/g, '/')

  // Add padding if needed
  while (str.length % 4) {
    str += '='
  }

  try {
    return atob(str)
  } catch (error) {
    throw new Error('Invalid base64 string')
  }
}

/**
 * Decode JWT token without verification
 * Note: This only decodes the payload, it doesn't verify the signature
 */
export function decodeJWT(token: string): JWTPayload {
  if (!token || typeof token !== 'string') {
    throw new Error('Invalid token: token must be a non-empty string')
  }

  const parts = token.split('.')
  if (parts.length !== 3) {
    throw new Error('Invalid token: JWT must have 3 parts')
  }

  try {
    const payload = base64UrlDecode(parts[1])
    return JSON.parse(payload) as JWTPayload
  } catch (error) {
    throw new Error('Invalid token: Unable to decode payload')
  }
}

/**
 * Check if a JWT token is expired
 */
export function isTokenExpired(token: string, bufferMinutes: number = 0): boolean {
  // Check if this is a JWT token (3 parts separated by dots)
  if (token.split('.').length !== 3) {
    // For non-JWT tokens (like Laravel Sanctum), return false
    // Expiration should be handled by the calling code using stored timestamps
    return false
  }

  try {
    const payload = decodeJWT(token)

    if (!payload.exp) {
      // If no expiration claim, consider it expired for safety
      return true
    }

    const now = Math.floor(Date.now() / 1000) // Current time in seconds
    const buffer = bufferMinutes * 60 // Convert minutes to seconds

    return payload.exp <= now + buffer
  } catch (error) {
    // If we can't decode the token, consider it expired
    return true
  }
}

/**
 * Get token expiration time in milliseconds
 */
export function getTokenExpiration(token: string): number | null {
  try {
    const payload = decodeJWT(token)
    return payload.exp ? payload.exp * 1000 : null // Convert to milliseconds
  } catch (error) {
    return null
  }
}

/**
 * Get time until token expires in milliseconds
 */
export function getTimeUntilExpiration(token: string): number {
  const expiration = getTokenExpiration(token)
  if (!expiration) {
    return 0
  }

  return Math.max(0, expiration - Date.now())
}

/**
 * Check if token needs refresh (within refresh window)
 */
export function shouldRefreshToken(token: string, refreshMinutesBefore: number = 10): boolean {
  return isTokenExpired(token, refreshMinutesBefore)
}

/**
 * Extract token information including expiration details
 */
export function getTokenInfo(token: string, refreshToken: string): TokenInfo {
  try {
    const payload = decodeJWT(token)

    return {
      token,
      refreshToken,
      expiresAt: payload.exp ? payload.exp * 1000 : 0,
      issuedAt: payload.iat ? payload.iat * 1000 : Date.now(),
    }
  } catch (error) {
    throw new Error('Invalid token: Unable to extract token information')
  }
}

/**
 * Validate token format and basic structure
 */
export function isValidTokenFormat(token: string): boolean {
  if (!token || typeof token !== 'string') {
    return false
  }

  const parts = token.split('.')
  if (parts.length !== 3) {
    return false
  }

  try {
    // Try to decode the header and payload
    base64UrlDecode(parts[0]) // header
    base64UrlDecode(parts[1]) // payload
    return true
  } catch (error) {
    return false
  }
}

/**
 * Get user ID from token
 */
export function getUserIdFromToken(token: string): string | null {
  try {
    const payload = decodeJWT(token)
    return payload.sub || null
  } catch (error) {
    return null
  }
}

/**
 * Check if token is close to expiration (for warning purposes)
 */
export function isTokenNearExpiration(token: string, warningMinutes: number = 5): boolean {
  const timeUntilExpiration = getTimeUntilExpiration(token)
  const warningThreshold = warningMinutes * 60 * 1000 // Convert to milliseconds

  return timeUntilExpiration > 0 && timeUntilExpiration <= warningThreshold
}

/**
 * Format expiration time for display
 */
export function formatTokenExpiration(token: string): string {
  const expiration = getTokenExpiration(token)
  if (!expiration) {
    return 'Unknown'
  }

  return new Date(expiration).toLocaleString()
}

/**
 * Calculate refresh schedule timing
 */
export function calculateRefreshTiming(
  token: string,
  refreshMinutesBefore: number = 10,
): {
  shouldRefresh: boolean
  timeUntilRefresh: number
  timeUntilExpiration: number
} {
  const timeUntilExpiration = getTimeUntilExpiration(token)
  const refreshThreshold = refreshMinutesBefore * 60 * 1000 // Convert to milliseconds
  const timeUntilRefresh = Math.max(0, timeUntilExpiration - refreshThreshold)

  return {
    shouldRefresh: timeUntilRefresh <= 0 && timeUntilExpiration > 0,
    timeUntilRefresh,
    timeUntilExpiration,
  }
}

/**
 * Combined token information interface
 */
export interface CombinedTokenInfo {
  jwt: TokenInfo
  csrf: CSRFTokenInfo | null
  isValid: boolean
  needsRefresh: boolean
}

/**
 * Get combined JWT and CSRF token information
 */
export function getCombinedTokenInfo(
  jwtToken: string,
  refreshToken: string,
  csrfTokenInfo: CSRFTokenInfo | null = null,
): CombinedTokenInfo {
  try {
    const jwt = getTokenInfo(jwtToken, refreshToken)
    const jwtNeedsRefresh = shouldRefreshToken(jwtToken)
    const csrfNeedsRefresh = csrfTokenInfo
      ? csrfTokenInfo.expiresAt
        ? Date.now() >= csrfTokenInfo.expiresAt - 10 * 60 * 1000
        : false
      : true

    return {
      jwt,
      csrf: csrfTokenInfo,
      isValid:
        !isTokenExpired(jwtToken) && (csrfTokenInfo ? !isCSRFTokenExpired(csrfTokenInfo) : true),
      needsRefresh: jwtNeedsRefresh || csrfNeedsRefresh,
    }
  } catch (error) {
    return {
      jwt: {} as TokenInfo,
      csrf: csrfTokenInfo,
      isValid: false,
      needsRefresh: true,
    }
  }
}

/**
 * Check if CSRF token is expired
 */
export function isCSRFTokenExpired(
  csrfTokenInfo: CSRFTokenInfo,
  bufferMinutes: number = 5,
): boolean {
  if (!csrfTokenInfo.expiresAt) return false // No expiration means token is valid

  const now = Date.now()
  const bufferMs = bufferMinutes * 60 * 1000
  return now >= csrfTokenInfo.expiresAt - bufferMs
}

/**
 * Check if CSRF token should be refreshed
 */
export function shouldRefreshCSRFToken(
  csrfTokenInfo: CSRFTokenInfo,
  refreshMinutesBefore: number = 10,
): boolean {
  if (!csrfTokenInfo.expiresAt) return false // No expiration means no need to refresh

  const now = Date.now()
  const refreshTime = csrfTokenInfo.expiresAt - refreshMinutesBefore * 60 * 1000
  return now >= refreshTime
}

/**
 * Validate combined token security
 */
export function validateCombinedTokenSecurity(
  jwtToken: string,
  csrfTokenInfo: CSRFTokenInfo | null,
  requireCSRF: boolean = true,
): {
  isValid: boolean
  errors: string[]
} {
  const errors: string[] = []

  // Validate JWT token
  if (!isValidTokenFormat(jwtToken)) {
    errors.push('Invalid JWT token format')
  } else if (isTokenExpired(jwtToken)) {
    errors.push('JWT token is expired')
  }

  // Validate CSRF token if required
  if (requireCSRF) {
    if (!csrfTokenInfo) {
      errors.push('CSRF token is missing')
    } else {
      if (!csrfTokenInfo.token || csrfTokenInfo.token.length < 16) {
        errors.push('Invalid CSRF token format')
      } else if (isCSRFTokenExpired(csrfTokenInfo)) {
        errors.push('CSRF token is expired')
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}
